












import {MetaInfo} from 'vue-meta'
import {Component, Vue} from 'vue-property-decorator'
import {PortalTarget} from 'portal-vue'
import {WalletPlatform} from '@/enums/WalletPlatform'
import {isNeoLineInAppBrowser} from '@/store/model/wallets/connection/NeoLineConnection'

Component.registerHooks(['metaInfo'])

@Component({
  components: {PortalTarget},
})
export default class App extends Vue {
  metaInfo(): MetaInfo {
    const description = this.$translate('metadata.defaultDescription') as string
    const keywords = this.$translate('metadata.defaultKeywords') as string
    const author = this.$translate('metadata.author') as string
    const publisher = this.$translate('metadata.publisher') as string
    const canonical = this.canonicalUrl()
    const ogTitle = this.openGraphTitle()

    return {
      title: this.$translate('app.title') as string,
      htmlAttrs: {
        lang: (this.$i18n?.locale as string) || 'en',
      },
      meta: [
        {vmid: 'description', name: 'description', content: description},
        {vmid: 'keywords', name: 'keywords', content: keywords},
        {vmid: 'author', name: 'author', content: author},
        {vmid: 'publisher', name: 'publisher', content: publisher},
        {vmid: 'robots', name: 'robots', content: 'index, follow'},
        {vmid: 'og:type', property: 'og:type', content: 'website'},
        {
          vmid: 'og:site_name',
          property: 'og:site_name',
          content: this.$translate('app.title') as string,
        },
        {vmid: 'og:url', property: 'og:url', content: canonical},
        {vmid: 'og:title', property: 'og:title', content: ogTitle},
        {
          vmid: 'og:description',
          property: 'og:description',
          content: description,
        },
        {vmid: 'twitter:card', name: 'twitter:card', content: 'summary'},
        {vmid: 'twitter:title', name: 'twitter:title', content: ogTitle},
        {
          vmid: 'twitter:description',
          name: 'twitter:description',
          content: description,
        },
      ],
      link: canonical ? [{rel: 'canonical', href: canonical}] : [],
    }
  }

  canonicalUrl(): string {
    const configured = String(process.env.VUE_APP_SITE_URL || '').replace(
      /\/$/,
      ''
    )
    const path = this.$route?.fullPath || '/'
    const normalized = path.startsWith('/') ? path : `/${path}`
    if (configured) {
      return `${configured}${normalized}`
    }
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${normalized}`
    }
    return ''
  }

  /** Fallback OG title when the active route has no dedicated title key. */
  openGraphTitle(): string {
    const routeName = this.$route?.name as string | undefined
    const titleKeyByRoute: Record<string, string> = {
      home: 'metadata.title.home',
      dappsOverview: 'metadata.title.dappsOverview',
      appsOverview: 'metadata.title.appsOverview',
      contracts: 'metadata.title.contracts',
      wallets: 'metadata.title.wallets',
      explorers: 'metadata.title.explorers',
      exchanges: 'metadata.title.exchanges',
      tokens: 'metadata.title.tokens',
      nfts: 'metadata.title.nfts',
      gasbot: 'metadata.title.gasbot',
      communityDevelopment: 'metadata.title.communityDevelopment',
      termsAndConditions: 'metadata.title.termsAndConditions',
      privacyPolicy: 'metadata.title.privacyPolicy',
      contactUs: 'metadata.title.contactUs',
      advancedGasCalculator: 'metadata.title.gasCalculator',
      getGas: 'metadata.title.getGas',
      orbit: 'metadata.title.orbit',
      myWallet: 'metadata.title.myWallet',
      bridge: 'metadata.title.bridge',
    }
    const key = routeName ? titleKeyByRoute[routeName] : undefined
    if (key) {
      return this.$translate(key) as string
    }
    return this.$translate('metadata.title.home') as string
  }

  async created() {
    try {
      await this.$auth.loadGlobal()
    } catch (_) {
      // If the API is unavailable, keep defaults (features enabled by default).
    }

    const n3Init = this.$walletAdapter.initN3Wallet()
    this.$walletAdapter.initXWallet()

    void Promise.resolve(n3Init).then(() => this.autoConnectNeoLineInApp())
  }

  /** Auto-connect NeoLine N3 when running inside the NeoLine in-app browser. */
  private async autoConnectNeoLineInApp() {
    if (!isNeoLineInAppBrowser()) return
    if (this.$walletAdapter.n3Address) return

    try {
      await this.$walletAdapter.connectN3Wallet(WalletPlatform.NEOLINE)
    } catch (_) {
      // Ignore — the user can still connect manually from the wallet modal.
    }
  }
}
